import { type FC, useCallback, useMemo } from 'react';
import { useSelector } from 'react-redux';

import { prepareCarouselBanners } from 'shared/mappers/prepareCarouselBanners';
import { bannersByRegionIdSelector } from 'shared/selectors/banners/bannersByRegionIdSelector';
import { getFilterLocation } from 'shared/selectors/filters/getFilterLocation';
import { newbuildingsPlusBlocksPikSelector } from 'shared/selectors/specialPromosBlocks/newbuildingsPlusBlocksPikSelector';
import { type MainCarouselSlidesDuration } from 'shared/types/mainCarousel';
import { useApplicationContext } from 'shared/utils/applicationContext';

import { CarouselMainV2 } from './CarouselMainV2';
import { trackMainCarouselProgressDotClick } from './internal/tracking/trackMainCarouselProgressDotClick';
import { trackMainCarouselSelectApartmentClick } from './internal/tracking/trackMainCarouselSelectApartmentClick';
import { trackShowMainCarouselSection } from './internal/tracking/trackShowMainCarouselSection';

export const BLOCK_ID = 'main-carousel';

export const CarouselMainV2Container: FC = () => {
  const { config } = useApplicationContext();

  const slidesDuration = config.getStrict<MainCarouselSlidesDuration>('Martech.PikLanding.MainCarousel.SlidesDuration');

  const bannersByRegionId = useSelector(bannersByRegionIdSelector);
  const selectedRegion = useSelector(getFilterLocation);
  const newbuildingsPlusBlocksPik = useSelector(newbuildingsPlusBlocksPikSelector);

  const [banners, intervals] = useMemo(
    () => prepareCarouselBanners({ bannersByRegionId, newbuildingsPlusBlocksPik, slidesDuration }),
    [bannersByRegionId, newbuildingsPlusBlocksPik, slidesDuration],
  );

  const handleView = useCallback(() => trackShowMainCarouselSection(banners), [banners]);

  const handleDotClick = useCallback(
    (index: number) => trackMainCarouselProgressDotClick(banners[index].id, index),
    [banners],
  );

  const handleSelectApartmentClick = useCallback(
    (newbuildingId: number, index: number) => trackMainCarouselSelectApartmentClick(newbuildingId, index),
    [],
  );

  return (
    <CarouselMainV2
      banners={banners}
      interval={intervals}
      key={selectedRegion}
      testId="main-carousel"
      onDotClick={handleDotClick}
      onSelectApartmentClick={handleSelectApartmentClick}
      onView={handleView}
    />
  );
};
